import { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Star, MapPin, Calendar, Phone, Menu, X } from 'lucide-react';
import { Button } from '@/components/ui/button';

const Reviews = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  const reviews = [
    {
      name: 'Michael Rodriguez',
      city: 'Orlando, FL',
      rating: 5,
      date: '2026-03-01',
      dateDisplay: '2 weeks ago',
      text: 'Jason and his team did an incredible job on our roof coating! Our AC bill dropped by almost 30% in the first month. The house stays so much cooler now. Highly recommend Savage Rejuvenation!',
      service: 'Cool Roof Coating - Asphalt Shingles'
    },
    {
      name: 'Sarah Chen',
      city: 'Tampa, FL',
      rating: 5,
      date: '2026-02-15',
      dateDisplay: '1 month ago',
      text: 'Professional, punctual, and the results are amazing. Our metal roof looks brand new and the heat reduction is noticeable. Worth every penny!',
      service: 'Metal Roof Restoration'
    },
    {
      name: 'David Thompson',
      city: 'Lakeland, FL',
      rating: 5,
      date: '2026-02-22',
      dateDisplay: '3 weeks ago',
      text: 'Lainie was so helpful throughout the entire process. Quick quote, fair pricing, and they finished in just one day! Our tile roof is now waterproof and looks fantastic.',
      service: 'Tile Roof Sealing'
    },
    {
      name: 'Jennifer Martinez',
      city: 'Winter Park, FL',
      rating: 5,
      date: '2026-01-15',
      dateDisplay: '2 months ago',
      text: 'Best investment we made for our home. The coating survived the last hurricane without any issues. Our roof is protected and our energy bills are way down!',
      service: 'Hurricane Protection Coating'
    },
    {
      name: 'Robert Wilson',
      city: 'Kissimmee, FL',
      rating: 5,
      date: '2026-02-01',
      dateDisplay: '1 month ago',
      text: 'Family-owned business that truly cares. Jason took the time to explain everything and the crew was respectful and efficient. A+ service!',
      service: 'Roof Rejuvenation - Asphalt'
    },
    {
      name: 'Amanda Foster',
      city: 'Clearwater, FL',
      rating: 5,
      date: '2026-02-10',
      dateDisplay: '5 weeks ago',
      text: 'Called for a quote and they came out the same day! Incredibly responsive and the work quality is outstanding. My neighbors are already asking for their contact info.',
      service: 'Cool Roof Coating'
    },
    {
      name: 'Carlos Mendez',
      city: 'St. Petersburg, FL',
      rating: 5,
      date: '2026-01-28',
      dateDisplay: '6 weeks ago',
      text: 'Third roofer I contacted but the only one who took time to really explain the process. Fair pricing, no hidden fees, and excellent results. Highly recommend!',
      service: 'Metal Roof Coating'
    }
  ];

  const cities = [
    { name: 'Orlando', count: 47 },
    { name: 'Tampa', count: 38 },
    { name: 'Lakeland', count: 21 },
    { name: 'Kissimmee', count: 19 },
    { name: 'Winter Park', count: 16 },
    { name: 'Clearwater', count: 14 },
    { name: 'St. Petersburg', count: 12 },
    { name: 'Sanford', count: 9 },
    { name: 'Daytona Beach', count: 8 },
    { name: 'Melbourne', count: 7 },
    { name: 'Ocala', count: 5 }
  ];

  // LocalBusiness with AggregateRating Schema
  const reviewsSchema = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "@id": "https://savagerejuvenationcoatings.com/#business",
    "name": "Savage Rejuvenation Coatings",
    "image": "https://customer-assets.emergentagent.com/job_rejuv-hyper-local/artifacts/s2yzmkus_IMG_2472.png",
    "telephone": "+1-321-804-4413",
    "address": {
      "@type": "PostalAddress",
      "addressLocality": "Orlando",
      "addressRegion": "FL",
      "addressCountry": "US"
    },
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "5.0",
      "reviewCount": "200",
      "bestRating": "5",
      "worstRating": "1"
    },
    "review": reviews.map(review => ({
      "@type": "Review",
      "reviewRating": {
        "@type": "Rating",
        "ratingValue": review.rating.toString(),
        "bestRating": "5"
      },
      "author": {
        "@type": "Person",
        "name": review.name
      },
      "reviewBody": review.text,
      "datePublished": review.date
    }))
  };

  // Breadcrumb Schema
  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": "https://savagerejuvenationcoatings.com"
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "Reviews",
        "item": "https://savagerejuvenationcoatings.com/reviews"
      }
    ]
  };

  return (
    <>
      <Helmet>
        <title>Customer Reviews | 5-Star Roof Coating Orlando Tampa FL | Savage Rejuvenation Coatings</title>
        <meta name="description" content="Read 200+ verified 5-star reviews for Savage Rejuvenation Coatings. See why Central Florida homeowners trust us for roof coating in Orlando, Tampa, Lakeland, Kissimmee & beyond. Real results, real savings!" />
        <meta name="keywords" content="Savage Rejuvenation reviews, roof coating reviews Orlando, Tampa roof coating testimonials, best roofer Central Florida reviews, 5 star roofing company Orlando" />
        
        <meta property="og:title" content="200+ 5-Star Reviews | Savage Rejuvenation Coatings" />
        <meta property="og:description" content="See why Central Florida homeowners love our roof coating services. Real reviews, real results!" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://savagerejuvenationcoatings.com/reviews" />
        
        <link rel="canonical" href="https://savagerejuvenationcoatings.com/reviews" />
        <meta name="robots" content="index, follow" />
        
        <script type="application/ld+json">
          {JSON.stringify(reviewsSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbSchema)}
        </script>
      </Helmet>

      <div className="min-h-screen bg-white">
        <header className="sticky top-0 z-50 bg-secondary border-b-4 border-primary">
          <div className="max-w-7xl mx-auto px-4 md:px-12 py-3">
            <div className="flex items-center justify-between">
              <nav className="hidden lg:flex items-center gap-6">
                <a href="/" className="text-white hover:text-primary font-manrope font-semibold">Home</a>
                <a href="/reviews" className="text-primary font-manrope font-semibold">Reviews</a>
                <a href="/qa" className="text-white hover:text-primary font-manrope font-semibold">Q&A</a>
                <a href="/blog" className="text-white hover:text-primary font-manrope font-semibold">Blog</a>
              </nav>
              <div className="hidden lg:flex items-center gap-6">
                <a href="tel:3218044413" className="text-white hover:text-primary transition-colors flex items-center gap-2 text-lg font-manrope font-semibold">
                  <Phone className="h-5 w-5" />
                  (321) 804-4413
                </a>
              </div>
              <div className="lg:hidden flex items-center gap-3">
                <a href="tel:3218044413">
                  <Button className="bg-primary hover:bg-primary-hover text-white border-2 border-black shadow-[2px_2px_0px_0px_#000] rounded-sm px-3 py-2">
                    <Phone className="h-5 w-5" />
                  </Button>
                </a>
                <button 
                  onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                  className="text-white p-2"
                >
                  {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
                </button>
              </div>
            </div>
            {/* Mobile Menu */}
            {mobileMenuOpen && (
              <nav className="lg:hidden mt-4 pb-4 border-t border-gray-600 pt-4 flex flex-col gap-4">
                <a href="/" className="text-white hover:text-primary font-manrope font-semibold text-lg">Home</a>
                <a href="/reviews" className="text-primary font-manrope font-semibold text-lg">Reviews</a>
                <a href="/qa" className="text-white hover:text-primary font-manrope font-semibold text-lg">Q&A</a>
                <a href="/blog" className="text-white hover:text-primary font-manrope font-semibold text-lg">Blog</a>
                <a href="tel:3218044413" className="text-primary font-manrope font-semibold text-lg flex items-center gap-2">
                  <Phone className="h-5 w-5" />
                  (321) 804-4413
                </a>
              </nav>
            )}
          </div>
        </header>

        <section className="py-20 bg-gradient-to-b from-primary/10 to-white">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <div className="text-center mb-12">
              <h1 className="font-barlow text-5xl font-bold uppercase mb-4">Customer Reviews</h1>
              <div className="flex items-center justify-center gap-2 mb-4">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="h-8 w-8 fill-primary text-primary" />
                ))}
              </div>
              <p className="text-2xl font-manrope text-gray-700">5.0 Stars from 200+ Happy Customers</p>
            </div>

            <div className="grid md:grid-cols-2 gap-8 mb-12">
              {reviews.map((review, idx) => (
                <div key={idx} className="bg-white border-2 border-black shadow-[4px_4px_0px_0px_#000] p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <h3 className="font-barlow text-xl font-bold">{review.name}</h3>
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <MapPin className="h-4 w-4" />
                        <span>{review.city}</span>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="flex gap-1 mb-1">
                        {[...Array(review.rating)].map((_, i) => (
                          <Star key={i} className="h-4 w-4 fill-primary text-primary" />
                        ))}
                      </div>
                      <div className="flex items-center gap-1 text-xs text-gray-500">
                        <Calendar className="h-3 w-3" />
                        {review.dateDisplay}
                      </div>
                    </div>
                  </div>
                  <p className="font-manrope text-gray-700 mb-3">{review.text}</p>
                  <div className="text-sm text-primary font-semibold">{review.service}</div>
                </div>
              ))}
            </div>

            <div className="bg-accent-light border-2 border-black shadow-[6px_6px_0px_0px_#000] p-8">
              <h2 className="font-barlow text-3xl font-bold uppercase mb-6 text-center">Reviews by Location</h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {cities.map((city, idx) => (
                  <div key={idx} className="bg-white border-2 border-black shadow-[2px_2px_0px_0px_#000] p-4 text-center">
                    <p className="font-barlow text-2xl font-bold text-primary">{city.count}</p>
                    <p className="font-manrope font-semibold text-sm">{city.name}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="py-16 bg-secondary text-white">
          <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
            <h2 className="font-barlow text-4xl font-bold uppercase mb-4">Ready to Join Our Happy Customers?</h2>
            <p className="text-xl font-manrope mb-8">Get your FREE inspection today!</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href="tel:3218044413" className="bg-primary hover:bg-primary-hover text-white px-8 py-4 border-2 border-white shadow-[4px_4px_0px_0px_#fff] font-barlow uppercase font-bold text-lg inline-block">
                Call: (321) 804-4413
              </a>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default Reviews;